import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import { SlideOverPanel, StatusPill, SegmentedPills } from '../systemKit';
import { describeAlert } from '../../lib/shrinkflation';
import PremiumLineChart, { fmtDateShort } from '../PremiumLineChart';

// ─── Volume Detail Drawer (Price Tracker, Volume mode) ──────────────────────
// Slides in from the right when an item row is clicked in Volume mode. Goal:
// give the operator a one-glance answer to "why is this changing?" — without
// leaving Price Tracker, without category-level context (Inventory Trends'
// job), without forecasting.
//
// Layout (top → bottom):
//   1. Header (item name + category + catch-weight indicator)
//   2. Headline — one-sentence plain-language insight
//   3. Orders this period vs prior period (side-by-side)
//   4. Total volume change (delta in lbs or cases)
//   5. Metric toggle (Volume | $/lb | Spend) + SVG line chart (shared with
//      Inventory Trends drawer via src/components/PremiumLineChart.js).
//   6. Related signals (conditional: shrinkflation, needs_review, price change, vendor change)
//   7. Italic disclosure footer
//
// One round-trip data fetch on open. See DESIGN_SYSTEM.md §Volume detail
// drawer.

function fmtNumber(n, opts = {}) {
  if (n == null || !isFinite(n)) return '\u2014';
  return Number(n).toLocaleString('en-US', opts);
}

// Compute same-length prior range (matches the existing Volume table pattern
// in FoodPriceTracker — Price Tracker is single-mode, not period-aware
// DateStepper, so calendar-period semantics don't apply here).
function computePriorRange(rangeStart, rangeEnd) {
  const start = new Date(rangeStart + 'T00:00:00');
  const end = new Date(rangeEnd + 'T00:00:00');
  const days = Math.max(1, Math.round((end - start) / 86400000) + 1);
  const priorEnd = new Date(start); priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd); priorStart.setDate(priorStart.getDate() - (days - 1));
  return {
    start: priorStart.toISOString().split('T')[0],
    end: priorEnd.toISOString().split('T')[0],
  };
}

export default function VolumeDetailDrawer({
  open, onClose,
  companyId,
  item,           // food_items row (carries needs_review, is_catch_weight, etc.)
  range,          // { start, end } — current period
  useWeight,      // computed by caller; mirrors the row's display unit
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { lines, alert, priorRange }
  const [metric, setMetric] = useState('volume'); // 'volume' | 'perLb' | 'spend'

  const priorRange = useMemo(() => range ? computePriorRange(range.start, range.end) : null, [range]);

  useEffect(() => {
    if (!open || !item || !companyId || !range || !priorRange) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [linesRes, alertsRes] = await Promise.all([
        supabase.from('invoice_line_items')
          .select('id, invoice_id, invoice_date, vendor_name, quantity, unit, total_weight, price_per_lb, unit_price, line_total, pack_count, piece_weight')
          .eq('company_id', companyId)
          .eq('food_item_id', item.id)
          .gte('invoice_date', priorRange.start)
          .lte('invoice_date', range.end)
          .order('invoice_date', { ascending: true }),
        supabase.from('shrinkflation_alerts')
          .select('*')
          .eq('company_id', companyId)
          .eq('food_item_id', item.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      setData({
        lines: linesRes.data || [],
        alert: alertsRes.data || null,
        priorRange,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, item, companyId, range, priorRange]);

  // Roll-up.
  const stats = useMemo(() => {
    if (!data || !range || !priorRange) return null;
    const lines = data.lines || [];
    const inCurrent = (l) => l.invoice_date >= range.start && l.invoice_date <= range.end;
    const inPrior = (l) => l.invoice_date >= priorRange.start && l.invoice_date <= priorRange.end;

    const currLines = lines.filter(inCurrent);
    const priorLines = lines.filter(inPrior);

    const orders = (ls) => new Set(ls.map(l => l.invoice_id).filter(v => v != null)).size;
    const volume = (ls) => useWeight
      ? ls.reduce((s, l) => s + (Number(l.total_weight) || 0), 0)
      : ls.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
    const avgPerLb = (ls) => {
      const vals = ls.map(l => Number(l.price_per_lb)).filter(v => v > 0);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const avgUnitPrice = (ls) => {
      const vals = ls.map(l => Number(l.unit_price)).filter(v => v > 0);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const topVendor = (ls) => {
      const counts = {};
      ls.forEach(l => { if (l.vendor_name) counts[l.vendor_name] = (counts[l.vendor_name] || 0) + 1; });
      const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      return best ? best[0] : null;
    };

    return {
      currOrders: orders(currLines),
      priorOrders: orders(priorLines),
      currVolume: volume(currLines),
      priorVolume: volume(priorLines),
      currPerLb: avgPerLb(currLines),
      priorPerLb: avgPerLb(priorLines),
      currUnit: avgUnitPrice(currLines),
      priorUnit: avgUnitPrice(priorLines),
      currVendor: topVendor(currLines),
      priorVendor: topVendor(priorLines),
    };
  }, [data, range, priorRange, useWeight]);

  // Headline string — derived from observable data only.
  const headline = useMemo(() => {
    if (!stats) return '';
    const unit = useWeight ? 'lbs' : 'cases';
    const cV = stats.currVolume;
    const pV = stats.priorVolume;
    const cO = stats.currOrders;
    const pO = stats.priorOrders;

    if (pV === 0 && cV === 0) return 'No orders in either period.';
    if (pV === 0 && cV > 0) return `No prior-period data yet — more orders needed to see trend. ${cO} order${cO === 1 ? '' : 's'} this period (${fmtNumber(Math.round(cV))} ${unit}).`;
    if (cV === 0 && pV > 0) return `No orders this period — prior period had ${pO} order${pO === 1 ? '' : 's'} (${fmtNumber(Math.round(pV))} ${unit}).`;

    const pct = ((cV - pV) / pV) * 100;
    const dir = pct >= 0 ? 'up' : 'down';
    const orderDelta = cO - pO;
    const orderClause = orderDelta === 0
      ? 'order frequency unchanged'
      : `you ordered this ${Math.abs(orderDelta)} ${Math.abs(orderDelta) === 1 ? 'time' : 'times'} ${orderDelta > 0 ? 'more' : 'fewer'} than last period`;
    return `Volume ${dir} ${Math.abs(pct).toFixed(0)}% this period \u2014 ${orderClause}.`;
  }, [stats, useWeight]);

  // Related signals — priority order: shrinkflation, needs_review, price, vendor.
  const signals = useMemo(() => {
    if (!stats || !data) return [];
    const out = [];
    if (data.alert) {
      out.push({ kind: 'shrinkflation', label: `\u26A0 Shrinkflation alert \u2014 ${describeAlert(data.alert)}` });
    }
    if (item?.needs_review) {
      out.push({ kind: 'needs_review', label: '\u26A0 This item has a data review flag' });
    }
    // Price signal: use $/lb when available, else $/case. Only fire >5%.
    const hasPerLb = stats.currPerLb != null && stats.priorPerLb != null && stats.priorPerLb > 0;
    const hasCasePrice = stats.currUnit != null && stats.priorUnit != null && stats.priorUnit > 0;
    if (hasPerLb) {
      const pricePct = ((stats.currPerLb - stats.priorPerLb) / stats.priorPerLb) * 100;
      if (Math.abs(pricePct) >= 5) {
        out.push({ kind: 'price', label: `Price ${pricePct >= 0 ? 'up' : 'down'} ${Math.abs(pricePct).toFixed(0)}% this period vs last ($/lb)` });
      }
    } else if (hasCasePrice) {
      const pricePct = ((stats.currUnit - stats.priorUnit) / stats.priorUnit) * 100;
      if (Math.abs(pricePct) >= 5) {
        out.push({ kind: 'price', label: `Price ${pricePct >= 0 ? 'up' : 'down'} ${Math.abs(pricePct).toFixed(0)}% this period vs last ($/case)` });
      }
    }
    if (stats.currVendor && stats.priorVendor && stats.currVendor !== stats.priorVendor) {
      const fmtV = (v) => v ? String(v).replace(/\n/g, ' \u00B7 ') : '';
      out.push({ kind: 'vendor', label: `Ordered from ${fmtV(stats.currVendor)} this period (was ${fmtV(stats.priorVendor)} last period)` });
    }
    return out;
  }, [stats, data, item]);

  const subtitle = item ? (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {item.category && <span>{item.category}</span>}
      {item.is_catch_weight && <StatusPill tone="neutral">catch-weight</StatusPill>}
    </span>
  ) : null;

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={item?.normalized_name}
      subtitle={subtitle}
    >
      {!item ? null : loading || !stats ? (
        <div className="p-5 text-sm" style={{ color: 'var(--text-muted)' }}>{'Loading\u2026'}</div>
      ) : (
        <div className="p-5 space-y-5">
          {/* 2. Headline */}
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)', lineHeight: 1.4 }}>
            {headline}
          </div>

          {/* 3. Order frequency change */}
          <div
            className="rounded-md grid grid-cols-2 gap-4 p-4"
            style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
          >
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                This period
              </div>
              <div className="text-2xl font-semibold mt-1" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {stats.currOrders} {stats.currOrders === 1 ? 'order' : 'orders'}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {`${fmtDateShort(range.start)} \u2013 ${fmtDateShort(range.end)}`}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                Prior period
              </div>
              <div className="text-2xl font-semibold mt-1" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                {stats.priorOrders > 0 ? `${stats.priorOrders} ${stats.priorOrders === 1 ? 'order' : 'orders'}` : '\u2014'}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {`${fmtDateShort(priorRange.start)} \u2013 ${fmtDateShort(priorRange.end)}`}
              </div>
            </div>
          </div>

          {/* 4. Total volume change */}
          {(() => {
            const unit = useWeight ? 'lbs' : 'cases';
            const cV = stats.currVolume;
            const pV = stats.priorVolume;
            const delta = cV - pV;
            const pct = pV > 0 ? (delta / pV) * 100 : null;
            const sign = delta > 0 ? '+' : delta < 0 ? '\u2212' : '';
            const deltaColor = delta > 0 ? 'var(--text-success)' : delta < 0 ? 'var(--text-critical)' : 'var(--text-muted)';
            return (
              <div className="space-y-1">
                <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                  Volume
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>This period</div>
                  <div className="text-base font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtNumber(Math.round(cV))} {unit}
                  </div>
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Prior period</div>
                  <div className="text-base" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    {pV > 0 ? `${fmtNumber(Math.round(pV))} ${unit}` : '\u2014'}
                  </div>
                </div>
                <div className="flex items-baseline gap-3 flex-wrap pt-1">
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Change</div>
                  <div className="text-base font-semibold" style={{ color: deltaColor, fontVariantNumeric: 'tabular-nums' }}>
                    {pV > 0 ? `${sign}${fmtNumber(Math.abs(Math.round(delta)))} ${unit}${pct != null ? ` (${sign}${Math.abs(pct).toFixed(0)}%)` : ''}` : '\u2014'}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 5. Trend chart — metric toggle + shared SVG line chart from
              src/components/PremiumLineChart.js. Same chart treatment as the
              Inventory Trends drawer for visual consistency across "click an
              item to see its trend" surfaces. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                Trend
              </div>
              <SegmentedPills
                value={metric}
                onChange={setMetric}
                options={[
                  { key: 'volume', label: useWeight ? 'Volume (lbs)' : 'Volume (cases)' },
                  { key: 'perLb',  label: '$/lb' },
                  { key: 'spend',  label: 'Spend' },
                ]}
                size="sm"
              />
            </div>
            <PremiumLineChart
              series={(data.lines || []).map(l => {
                let value = null;
                if (metric === 'volume') value = useWeight ? Number(l.total_weight) || 0 : Number(l.quantity) || 0;
                else if (metric === 'perLb') value = l.price_per_lb != null ? Number(l.price_per_lb) : null;
                else if (metric === 'spend') value = Number(l.line_total) || 0;
                return { date: l.invoice_date, value };
              }).filter(p => p.value != null && isFinite(p.value))}
              range={range}
              priorRange={priorRange}
              yFormat={(v) => {
                if (metric === 'volume') return useWeight ? `${Math.round(v)} lb` : `${v.toFixed(1)} cs`;
                if (metric === 'perLb') return `$${v.toFixed(2)}`;
                if (metric === 'spend') return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;
                return String(v);
              }}
              gradientId="volume-drawer-chart-fill"
            />
          </div>

          {/* 6. Per-invoice history — same row format as Inventory Trends drawer
              for visual consistency. Semantic $/lb delta coloring (≥2%
              threshold) and prior-period rows muted to 70% opacity. */}
          {data.lines && data.lines.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Per-invoice history
              </div>
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <div className="grid text-xs" style={{
                  gridTemplateColumns: '88px 1.4fr 56px 70px 72px 90px',
                  background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
                }}>
                  {[
                    { label: 'Date', align: 'left' },
                    { label: 'Vendor', align: 'left' },
                    { label: 'Qty', align: 'right' },
                    { label: '$/cs', align: 'right' },
                    { label: '$/lb', align: 'right' },
                    { label: 'Pack', align: 'left' },
                  ].map(h => (
                    <div key={h.label} className="px-3 py-2"
                      style={{
                        color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                        textAlign: h.align,
                      }}
                    >{h.label}</div>
                  ))}
                </div>
                {data.lines.map((l, i) => {
                  const prev = i > 0 ? data.lines[i - 1] : null;
                  const perLb = l.price_per_lb != null ? Number(l.price_per_lb) : null;
                  const prevPerLb = prev?.price_per_lb != null ? Number(prev.price_per_lb) : null;
                  const perLbChanged = perLb != null && prevPerLb != null && Math.abs(perLb - prevPerLb) / prevPerLb >= 0.02;
                  const perLbColor = !perLbChanged
                    ? 'var(--text-primary)'
                    : (perLb > prevPerLb ? 'var(--text-critical)' : 'var(--text-success)');
                  const isCurrent = l.invoice_date >= range.start && l.invoice_date <= range.end;
                  // Pack format: pack_count=1 collapses to bare piece_weight ("50#"
                  // instead of "1×50#") matching Inventory Trends drawer's row format.
                  const packLabel = l.piece_weight && Number(l.pack_count) === 1
                    ? l.piece_weight
                    : (l.pack_count && l.piece_weight ? `${l.pack_count}\u00D7${l.piece_weight}` : (l.pack_count ? String(l.pack_count) : '\u2014'));
                  return (
                    <div key={i} className="grid text-xs" style={{
                      gridTemplateColumns: '88px 1.4fr 56px 70px 72px 90px',
                      borderBottom: i < data.lines.length - 1 ? '1px solid var(--border)' : 'none',
                      background: isCurrent ? 'transparent' : 'var(--bg-subtle)',
                      opacity: isCurrent ? 1 : 0.7,
                    }}>
                      <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtDateShort(l.invoice_date)}
                      </div>
                      <div className="px-3 py-2 truncate" style={{ color: 'var(--text-secondary)' }} title={l.vendor_name}>
                        {(l.vendor_name || '\u2014').replace(/\n/g, ' \u00B7 ')}
                      </div>
                      <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                        {Number(l.quantity || 0)}
                      </div>
                      <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                        {l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : '\u2014'}
                      </div>
                      <div className="px-3 py-2" style={{ color: perLbColor, fontVariantNumeric: 'tabular-nums', fontWeight: perLbChanged ? 600 : 400, textAlign: 'right' }}>
                        {perLb != null ? `$${perLb.toFixed(2)}` : '\u2014'}
                      </div>
                      <div className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>
                        {packLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
              {(data.lines || []).some(l => l.invoice_date < range.start) && (
                <div className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                  {`Greyed rows = prior period (${fmtDateShort(priorRange.start)}\u2013${fmtDateShort(priorRange.end)}) for context`}
                </div>
              )}
            </div>
          )}

          {/* 7. Related signals — only when present */}
          {signals.length > 0 && (
            <div className="space-y-2">
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                Related signals
              </div>
              <div className="space-y-1.5">
                {signals.map((s, i) => {
                  const onClick =
                    s.kind === 'shrinkflation'
                      ? () => { onClose?.(); setTimeout(() => document.querySelector('[data-vendor-watch-section]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250); }
                      : s.kind === 'needs_review'
                      ? () => { onClose?.(); setTimeout(() => document.querySelector('[data-data-review-section]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250); }
                      : null;
                  const tone =
                    s.kind === 'shrinkflation' || s.kind === 'needs_review' ? 'amber'
                    : s.kind === 'price' ? 'neutral'
                    : 'neutral';
                  return (
                    <div
                      key={i}
                      onClick={onClick || undefined}
                      className="text-sm rounded-md p-2 inline-flex items-start gap-2 w-full"
                      style={{
                        background: tone === 'amber' ? 'var(--bg-warning-subtle)' : 'var(--bg-subtle)',
                        border: '1px solid var(--border)',
                        cursor: onClick ? 'pointer' : 'default',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <span>{s.label}</span>
                      {onClick && <span className="ml-auto" style={{ color: 'var(--text-secondary)' }}>{'\u2192'}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 7. Disclosure */}
          <div className="pt-2 italic" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {'Posted sees your invoices, not your sales. If you know why this changed \u2014 new menu item, event, supplier swap \u2014 that context is yours to add.'}
          </div>
        </div>
      )}
    </SlideOverPanel>
  );
}
